import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import {
  WebhookCreationSource,
  WebhookDeliveriesTableFields,
  WebhookDeliveryStatus,
  WebhookEventType,
  WebhookResourceType,
  WebhookSubscriptionsTableFields,
  WebhookVersion,
} from './enums'

/** Mirrors backend `LATEST_WEBHOOK_VERSION`. The create form's default. */
export const LATEST_WEBHOOK_VERSION = WebhookVersion.V1

/**
 * Exhaustive over `WebhookEventType`, so a new backend event fails typechecking here rather
 * than rendering a raw key in the UI.
 */
export const webhookEventLabels: Record<WebhookEventType, string> = {
  [WebhookEventType.ContractorActivated]: 'Driver activated',
  [WebhookEventType.ContractorDeactivated]: 'Driver deactivated',
  [WebhookEventType.ContractorCreated]: 'Driver created',
  [WebhookEventType.ContractorUpdated]: 'Driver updated',
  [WebhookEventType.ContractorCollectedDataUpdate]: 'Driver onboarding data changed',
  [WebhookEventType.CollectedDataCreated]: 'Onboarding step started',
  [WebhookEventType.CollectedDataUpdated]: 'Onboarding step updated',
  [WebhookEventType.CollectedDataDeleted]: 'Onboarding step removed',
  [WebhookEventType.PaymentCreated]: 'Payment created',
  [WebhookEventType.PaymentUpdated]: 'Payment updated',
  [WebhookEventType.PaymentDeleted]: 'Payment deleted',
  [WebhookEventType.PaymentVoided]: 'Payment voided',
}

/** Groups the events multi-select by resource, in the order the backend enum declares them. */
export const webhookEventGroups: Array<{ label: string; events: WebhookEventType[] }> = [
  {
    label: 'Drivers',
    events: [
      WebhookEventType.ContractorActivated,
      WebhookEventType.ContractorDeactivated,
      WebhookEventType.ContractorCreated,
      WebhookEventType.ContractorUpdated,
      WebhookEventType.ContractorCollectedDataUpdate,
    ],
  },
  {
    label: 'Onboarding data',
    events: [
      WebhookEventType.CollectedDataCreated,
      WebhookEventType.CollectedDataUpdated,
      WebhookEventType.CollectedDataDeleted,
    ],
  },
  {
    label: 'Payments',
    events: [
      WebhookEventType.PaymentCreated,
      WebhookEventType.PaymentUpdated,
      WebhookEventType.PaymentDeleted,
      WebhookEventType.PaymentVoided,
    ],
  },
]

/** Flat option list for the send-test event select. Label carries the wire value for clarity. */
export const webhookEventOptions: Array<BasicSelectOption> = Object.values(WebhookEventType).map(
  (eventType) => {
    return { label: `${webhookEventLabels[eventType]} — ${eventType}`, value: eventType }
  },
)

export const webhookDeliveryStatusLabels: Record<WebhookDeliveryStatus, string> = {
  // The backend value is `success`; ops read it as a delivery outcome.
  [WebhookDeliveryStatus.Success]: 'Delivered',
  [WebhookDeliveryStatus.Pending]: 'Pending',
  [WebhookDeliveryStatus.Failed]: 'Failed',
}

export const webhookDeliveryStatusOptions: Array<BasicSelectOption> = Object.values(
  WebhookDeliveryStatus,
).map((status) => {
  return { label: webhookDeliveryStatusLabels[status], value: status }
})

export const webhookResourceTypeLabels: Record<WebhookResourceType, string> = {
  [WebhookResourceType.Contractor]: 'Driver',
  [WebhookResourceType.CollectedData]: 'Onboarding data',
  [WebhookResourceType.Payment]: 'Payment',
}

export const webhookResourceTypeOptions: Array<BasicSelectOption> = Object.values(
  WebhookResourceType,
).map((resourceType) => {
  return { label: webhookResourceTypeLabels[resourceType], value: resourceType }
})

export const webhookVersionLabels: Record<WebhookVersion, string> = {
  [WebhookVersion.V1]: 'Version 1',
  [WebhookVersion.V2]: 'Version 2',
}

export const webhookVersionOptions: Array<BasicSelectOption> = Object.values(WebhookVersion).map(
  (version) => {
    return { label: webhookVersionLabels[version], value: version }
  },
)

export const webhookCreationSourceLabels: Record<WebhookCreationSource, string> = {
  [WebhookCreationSource.Admin]: 'Admin',
  [WebhookCreationSource.Api]: 'Customer API',
}

export const webhookSubscriptionsTableHeaderTitles: Record<
  WebhookSubscriptionsTableFields,
  string
> = {
  [WebhookSubscriptionsTableFields.callbackUrl]: 'Callback URL',
  [WebhookSubscriptionsTableFields.events]: 'Events',
  [WebhookSubscriptionsTableFields.isActive]: 'Status',
  [WebhookSubscriptionsTableFields.creationSource]: 'Created by',
  [WebhookSubscriptionsTableFields.createdAt]: 'Date created',
  [WebhookSubscriptionsTableFields.id]: 'Subscription ID',
  // The actions column is icon-and-button only; a header would just add noise.
  [WebhookSubscriptionsTableFields.actions]: '',
}

export const webhookDeliveriesTableHeaderTitles: Record<WebhookDeliveriesTableFields, string> = {
  [WebhookDeliveriesTableFields.deliveryStatus]: 'Result',
  [WebhookDeliveriesTableFields.eventType]: 'Event',
  [WebhookDeliveriesTableFields.resourceId]: 'Resource',
  [WebhookDeliveriesTableFields.createdAt]: 'When',
  [WebhookDeliveriesTableFields.actions]: '',
}

// The delivery log is server-paginated and can be long, so it starts at 25 and stops at 100 —
// the backend's own default page is 20.
export const webhookDeliveryPageSizeOptions = [25, 50, 100]

// The subscriptions grid is client-paginated over a handful of rows; it gets its own constant
// rather than borrowing the deliveries one, which is named for a different table.
export const webhookSubscriptionPageSizeOptions = [25, 50, 100]

export const WEBHOOK_DELIVERIES_GRID_COLUMNS_STORAGE_KEY = 'admin.webhookDeliveries.gridColumns'

/** How many event labels a subscription row shows before collapsing into a count. */
export const WEBHOOK_EVENTS_CELL_VISIBLE_LIMIT = 2
